"use client";

import { createClient } from "@/lib/supabase/client";
import { getOtpTimer, resetOtpCookie } from "@/app/auth/otp-cookie";
import * as Sentry from "@sentry/nextjs";
import { generateRandomString } from "@/lib/utils/string-utils";
import posthog from "posthog-js";
import { identifyPosthogUser } from "@/lib/analytics/auth-tracking";

const OTP_COOLDOWN_SECONDS = 20;

export const requestLoginOtp = async (email: string) => {
  const supabase = createClient();
  try {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
      },
    });
    if (error) {
      // Do not catch Sentry error here
      // During registration flow, this is called before user is created
      // Getting an error here means user does not exist, and is part of positive flow
      // So we do not want to catch the error
      return {
        error: error.message || "Failed to send code. Please try again.",
      };
    }
    await resetOtpCookie(email);
    posthog.capture("otp_generated", { email, $set: { email } });
    return { success: "A new code has been sent to your email." };
  } catch (e) {
    return { error: "Something went wrong. Please try again." };
  }
};

export const loginWithEmailOtp = async (email: string, otp: string) => {
  const supabase = createClient();
  // Set user and add breadcrumb
  Sentry.setUser({
    email,
  });

  Sentry.addBreadcrumb({
    message: "Login with email OTP",
    category: "auth",
    level: "info",
  });

  try {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token: otp,
      type: "email",
    });
    if (error) {
      posthog.capture("login_failed", {
        email,
        error: error.message,
      });
      const isWrongOtp = error.message.includes("Token has expired or is invalid");
      return {
        error: isWrongOtp ? "Incorrect OTP. Please check and try again." : error.message,
      };
    }

    // Identify user in PostHog after successful login
    if (data.user) {
      identifyPosthogUser(data.user, "email_otp");
    }

    return { success: "Login successful!" };
  } catch (e) {
    Sentry.captureException(e);
    posthog.captureException(e);
    return { error: "Something went wrong. Please try again." };
  }
};

export const handleOtpResend = async (email: string) => {
  const supabase = createClient();
  // Set user and add breadcrumb
  Sentry.setUser({
    email,
  });

  Sentry.addBreadcrumb({
    message: "Verify OTP Cooldown",
    category: "auth",
    level: "info",
  });

  // Confirm that the user can resend the OTP
  const { canResend } = await getOtpTimer(email);
  if (!canResend) {
    return {
      error: `You can only resend the OTP every ${OTP_COOLDOWN_SECONDS} seconds.`,
    };
  }

  Sentry.addBreadcrumb({
    message: "Resend OTP using supabase",
    category: "auth",
    level: "info",
  });

  await resetOtpCookie(email);

  const { error } = await supabase.auth.signInWithOtp({
    email,
  });
  if (error) {
    Sentry.captureException(error);
    return { error: error.message || "Failed to resend code." };
  } else {
    posthog.capture("otp_generated", {
      email,
      is_resend: true,
      $set: { email },
    });
    return { success: "A new code has been sent to your email." };
  }
};

export const registerUser = async (username: string, email: string) => {
  const supabase = createClient();
  // Set user and add breadcrumb
  Sentry.setUser({
    name: username,
    email,
  });

  Sentry.addBreadcrumb({
    message: "Register user using supabase",
    category: "auth",
    level: "info",
  });

  const { data, error } = await supabase.auth.signUp({
    email,
    password: generateRandomString(12),
    options: {
      data: {
        full_name: username,
      },
    },
  });
  if (error) {
    Sentry.captureException(error);
    posthog.capture("registration_failed", {
      email,
      error: error.message,
    });
    return { error: error.message };
  }

  await resetOtpCookie(email);

  // Identify and capture registration event
  if (data.user) {
    identifyPosthogUser(data.user, "email_otp");
    posthog.capture("user_registered", {
      email,
      name: username,
      source: "registration_form",
    });
  }

  return { success: true };
};
